import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import DisplayDate from '../../components/DisplayDate';
import Button from '../../components/Button';
import DisplayDragIcon from '../../components/DisplayDragIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import NoInfoMessage from '../../components/NoInfoMessage';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { ChildSafeStandardAdminDashboardChildSafeStandard, ChildSafeStandardReorderCommand } from '../../api/models';

const ContentChildSafeStandards: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get all Standards Color from Redux Store
	const childSafeStandardColors = useAppSelector((state) => state.lists.childSafeStandardColors);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Child Safe Standards Id
	const [standardId, setStandardId] = React.useState<string>('');

	// State to store Child Safe Standards title
	const [standardTitle, setStandardTitle] = React.useState<string>('');

	// Set up Data state
	const [childSafetyStandardsData, setChildSafetyStandardsData] = React.useState<
		ChildSafeStandardAdminDashboardChildSafeStandard[]
	>([]);

	const getChildSafetyStandardData = async () => {
		setIsLoading(true);

		const response = await apibridge.getChildSafeStandardDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setChildSafetyStandardsData(result);
		}
	};

	React.useEffect(() => {
		getChildSafetyStandardData();
	}, []);

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(childSafetyStandardsData);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setChildSafetyStandardsData(items);

		const data: ChildSafeStandardReorderCommand = { id: reorderedItem.id, order: result.destination.index + 1 };

		apibridge.postChildSafeStandardReorder(data);
	};

	const removeHandler = async (item: ChildSafeStandardAdminDashboardChildSafeStandard) => {
		setStandardId(item.id || '');

		setStandardTitle(item.title || '');

		setShowRemoveModal(true);
	};

	const constantColorCode: string = '#9437BF';

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="single-btn-right-container border">
									<Link className="btn-primary btn-right" to="/content-child-safe-edit">
										<PlusIcon className="icon-link" /> Add new
									</Link>
								</div>

								<section className="info-section mtop-0">
									{childSafetyStandardsData && childSafetyStandardsData.length > 0 ? (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="contentChildSafeStandardsItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{childSafetyStandardsData.map((item, index) => {
															return (
																<Draggable
																	key={index}
																	draggableId={index.toString()}
																	index={index}
																	isDragDisabled={isOrganisationActive === false}
																>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<div className="row detail-row">
																				<div className="col-auto pdl-0 pdr-0">
																					<div
																						className="order-wrapper"
																						style={{
																							backgroundColor: `${
																								childSafeStandardColors && index < 11
																									? childSafeStandardColors[index].value
																									: constantColorCode
																							}`
																						}}
																					>
																						{index + 1}
																					</div>
																				</div>

																				<div className="col-md-auto full-width">
																					<div className="row detail-info">
																						<div className="col-10 col-md-6 detail-name">{item.title}</div>

																						<div className="col-10 col-md-6 text-category-detail">
																							{item.updated && <>Last updated &nbsp; &nbsp;</>}

																							{item.updated && (
																								<span className="text-subtitle">
																									<DisplayDate>{item.updated}</DisplayDate>
																								</span>
																							)}
																						</div>
																					</div>

																					<div className="d-block d-sm-none">
																						<Link
																							className="btn-text remove link"
																							to={`/content-child-safe-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>
																				</div>

																				<div className="col-auto pdr-0 link-group content-training-module-page">
																					<div className="d-none d-sm-flex">
																						<Link
																							className="btn-text remove link"
																							to={`/content-child-safe-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>

																					<div className="drag-icon-container">
																						<DisplayDragIcon index={index} array={childSafetyStandardsData} />
																					</div>
																				</div>
																			</div>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<NoInfoMessage name="Child Safe Standards" />
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove standard">
				<RemoveModal
					childSafetyStandardsData={childSafetyStandardsData}
					setChildSafetyStandardsData={setChildSafetyStandardsData}
					getChildSafetyStandardData={getChildSafetyStandardData}
					standardId={standardId}
					standardTitle={standardTitle}
				/>
			</GlobalModal>
		</div>
	);
};

export default ContentChildSafeStandards;
